// components/Employee/EmployeeList.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import EmployeeForm from './EmployeeForm';
import { employeeService } from '../../services/employeeService';
import { PencilIcon, TrashIcon, EyeIcon, EllipsisVerticalIcon, DocumentArrowDownIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import DataTable from 'react-data-table-component';

/* UI-only port to the Odoo-style chrome. Every piece of behaviour is
   unchanged from the previous version: same state, same service calls,
   same DataTable, same columns, same row actions, same CSV template
   download and upload modal, same search and stat counts.

   What changed is presentation only:
     - the rounded-2xl pink cards become one flat control-panel strip
       plus a single hairline-bordered table
     - pink accents become the teal --accent token
     - row density drops to Odoo's comfortable 8px/12.5px
     - colours resolve through theme.css vars, so this screen follows
       light/dark with no extra work
*/

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [actionDropdown, setActionDropdown] = useState(null);
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const dropdownRefs = useRef({});

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await employeeService.getEmployees();
      setEmployees(data);
    } catch (err) {
      console.error(err);
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    try {
      await employeeService.deleteEmployee(id);
      await loadEmployees();
      setActionDropdown(null);
    } catch (err) {
      console.error(err);
      setError('Failed to delete employee');
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setShowForm(true);
    setActionDropdown(null);
  };

  const handleView = (employee) => {
    const details = [
      `Employee Code: ${employee.employeeCode}`,
      `Name: ${employee.firstName} ${employee.lastName}`,
      `Email: ${employee.email}`,
      `Phone: ${employee.phone || 'Not provided'}`,
      `Position: ${employee.position || 'Not specified'}`,
      `Department: ${employee.department?.name || 'Not specified'}`,
      `Employment Type: ${getEmploymentTypeLabel(employee.employmentType)}`,
      `Status: ${employee.isActive ? 'Active' : 'Inactive'}`
    ].join('\n');
    alert(details);
    setActionDropdown(null);
  };

  const handleActionClick = (rowId, e) => {
    e.stopPropagation();
    setActionDropdown(actionDropdown === rowId ? null : rowId);
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await employeeService.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Employees_Template_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Failed to download template');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
    }
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setLoading(true);
      const results = await employeeService.uploadEmployees(uploadFile);
      setUploadResults(results);
      if (results.successCount > 0) {
        await loadEmployees();
      }
    } catch (err) {
      setError('Failed to upload employees');
    } finally {
      setLoading(false);
    }
  };

  const getEmploymentTypeLabel = (type) => {
    const types = {
      0: 'Full Time',
      1: 'Part Time',
      2: 'Contract',
      3: 'Intern'
    };
    return types[type] || 'Not specified';
  };

  // Filter employees based on search
  const filteredEmployees = useMemo(() => {
    if (!search.trim()) return employees;

    return employees.filter(employee =>
      employee.firstName?.toLowerCase().includes(search.toLowerCase()) ||
      employee.lastName?.toLowerCase().includes(search.toLowerCase()) ||
      employee.email?.toLowerCase().includes(search.toLowerCase()) ||
      employee.employeeCode?.toLowerCase().includes(search.toLowerCase()) ||
      employee.position?.toLowerCase().includes(search.toLowerCase())
    );
  }, [employees, search]);

  const badge = (tone) => ({
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    background: `var(--b-${tone}-bg)`,
    color: `var(--b-${tone}-fg)`
  });

  const ghostBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    height: 28,
    padding: '0 10px',
    background: 'var(--surface)',
    border: '1px solid var(--line-3)',
    borderRadius: 3,
    color: 'var(--body-2)',
    fontSize: 12.5,
    cursor: 'pointer'
  };

  // Define columns for DataTable
  const columns = [
    {
      name: '',
      selector: row => row.profilePictureUrl,
      cell: row => (
        <div className="flex items-center justify-center">
          {row.profilePictureUrl ? (
            <img
              src={row.profilePictureUrl}
              alt={`${row.firstName} ${row.lastName}`}
              className="h-7 w-7 rounded-full object-cover"
              style={{ border: '1px solid var(--line)' }}
            />
          ) : (
            <div
              className="flex h-7 w-7 items-center justify-center rounded-full text-[10.5px] font-semibold"
              style={{ background: 'var(--tint)', color: 'var(--tint-ink)' }}
            >
              {row.firstName?.charAt(0).toUpperCase()}{row.lastName?.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      ),
      width: '54px',
      center: true
    },
    {
      name: 'Employee',
      selector: row => `${row.firstName} ${row.lastName}`,
      sortable: true,
      cell: row => (
        <div className="min-w-0">
          <div className="truncate text-[12.5px] font-semibold" style={{ color: 'var(--ink)' }}>
            {row.firstName} {row.lastName}
          </div>
          <div className="text-[11px]" style={{ color: 'var(--faint)' }}>{row.employeeCode}</div>
        </div>
      ),
      minWidth: '180px',
      grow: 1
    },
    {
      name: 'Work email',
      selector: row => row.email,
      sortable: true,
      cell: row => (
        <a
          href={`mailto:${row.email}`}
          className="truncate text-[12.5px] hover:underline"
          style={{ color: 'var(--accent)' }}
        >
          {row.email}
        </a>
      ),
      minWidth: '200px'
    },
    {
      name: 'Position',
      selector: row => row.position,
      sortable: true,
      cell: row => (
        <span className="text-[12.5px]" style={{ color: row.position ? 'var(--body-2)' : 'var(--dim)' }}>
          {row.position || '—'}
        </span>
      ),
      minWidth: '150px'
    },
    {
      name: 'Department',
      selector: row => row.department?.name,
      sortable: true,
      cell: row => (
        <span className="text-[12.5px]" style={{ color: row.department ? 'var(--body-2)' : 'var(--dim)' }}>
          {row.department?.name || '—'}
        </span>
      ),
      minWidth: '150px'
    },
    {
      name: 'Type',
      selector: row => row.employmentType,
      sortable: true,
      cell: row => <span style={badge('blue')}>{getEmploymentTypeLabel(row.employmentType)}</span>,
      minWidth: '130px'
    },
    {
      name: 'Status',
      selector: row => row.isActive,
      sortable: true,
      cell: row => (
        <span style={badge(row.isActive ? 'green' : 'grey')}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
      minWidth: '110px'
    },
    {
      name: '',
      cell: row => (
        <div
          ref={el => dropdownRefs.current[row.id] = el}
          className="action-dropdown-container relative"
        >
          <button
            onClick={(e) => handleActionClick(row.id, e)}
            className="rounded-[3px] p-1.5 transition-colors"
            style={{ color: 'var(--dim)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--hover)'; e.currentTarget.style.color = 'var(--body)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--dim)'; }}
          >
            <EllipsisVerticalIcon className="h-4 w-4" />
          </button>

          {actionDropdown === row.id && (
            <div
              className="absolute right-0 z-50 mt-1 w-44 -translate-y-1 transform overflow-hidden rounded-[4px] py-1"
              style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow)' }}
            >
              <button
                onClick={(e) => { e.stopPropagation(); handleView(row); }}
                className="flex w-full items-center px-3 py-[7px] text-left text-[12.5px] transition-colors"
                style={{ color: 'var(--body)' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <EyeIcon className="mr-2.5 h-4 w-4" style={{ color: 'var(--accent)' }} />
                View Details
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
                className="flex w-full items-center px-3 py-[7px] text-left text-[12.5px] transition-colors"
                style={{ color: 'var(--body)' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <PencilIcon className="mr-2.5 h-4 w-4" style={{ color: 'var(--accent)' }} />
                Edit Employee
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}
                className="flex w-full items-center px-3 py-[7px] text-left text-[12.5px] transition-colors"
                style={{ color: 'var(--neg)', borderTop: '1px solid var(--line-soft)', marginTop: 4, paddingTop: 9 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <TrashIcon className="mr-2.5 h-4 w-4" />
                Delete Employee
              </button>
            </div>
          )}
        </div>
      ),
      width: '56px',
      ignoreRowClick: true,
      allowOverflow: true,
      button: true
    }
  ];

  // Custom styles for DataTable — flat, hairline, token-driven.
  const customStyles = {
    table: { style: { backgroundColor: 'var(--surface)' } },
    head: {
      style: {
        fontSize: '11.5px',
        fontWeight: '600',
        color: 'var(--muted)',
        backgroundColor: 'var(--surface-2)'
      }
    },
    headRow: {
      style: {
        minHeight: '32px',
        borderBottomWidth: '1px',
        borderBottomColor: 'var(--line)',
        backgroundColor: 'var(--surface-2)'
      }
    },
    headCells: {
      style: {
        paddingLeft: '10px',
        paddingRight: '10px',
        fontWeight: '600',
        letterSpacing: '0',
        textTransform: 'none',
        fontSize: '11.5px',
        color: 'var(--muted)',
        backgroundColor: 'var(--surface-2)'
      }
    },
    cells: {
      style: {
        paddingLeft: '10px',
        paddingRight: '10px',
        paddingTop: '8px',
        paddingBottom: '8px',
        fontSize: '12.5px',
        color: 'var(--body-2)'
      }
    },
    rows: {
      style: {
        minHeight: '40px',
        backgroundColor: 'var(--surface)',
        color: 'var(--body-2)',
        '&:not(:last-of-type)': {
          borderBottomWidth: '1px',
          borderBottomColor: 'var(--line-soft)'
        },
        '&:hover': { backgroundColor: 'var(--row-sel)' }
      },
      stripedStyle: { backgroundColor: 'var(--surface)' }
    },
    pagination: {
      style: {
        backgroundColor: 'var(--surface)',
        color: 'var(--muted)',
        fontSize: '12px',
        borderTopWidth: '1px',
        borderTopColor: 'var(--line)',
        minHeight: '42px'
      },
      pageButtonsStyle: {
        fill: 'var(--muted)',
        '&:disabled': { fill: 'var(--dim)' },
        '&:hover:not(:disabled)': { backgroundColor: 'var(--hover)' }
      }
    },
    noData: { style: { backgroundColor: 'var(--surface)' } },
    progress: { style: { backgroundColor: 'var(--surface)' } }
  };

  const LoadingComponent = () => (
    <div className="flex items-center justify-center py-12" style={{ background: 'var(--surface)' }}>
      <div
        className="h-6 w-6 animate-spin rounded-full border-b-2"
        style={{ borderColor: 'var(--accent)' }}
      />
      <span className="ml-3 text-[12.5px]" style={{ color: 'var(--muted)' }}>Loading employees…</span>
    </div>
  );

  const NoDataComponent = () => (
    <div
      className="flex flex-col items-center justify-center px-6 py-14 text-center"
      style={{ background: 'var(--surface)' }}
    >
      <div
        className="mb-4 flex h-12 w-12 items-center justify-center rounded-full"
        style={{ background: 'var(--tint)' }}
      >
        <DocumentArrowUpIcon className="h-6 w-6" style={{ color: 'var(--tint-ink)' }} />
      </div>
      <h3 className="mb-1 text-[14px] font-semibold" style={{ color: 'var(--ink)' }}>No employees found</h3>
      <p className="mb-4 text-[12.5px]" style={{ color: 'var(--faint)' }}>
        {search ? 'Try adjusting your search terms' : 'Get started by creating your first employee'}
      </p>
      {!search && (
        <button
          onClick={() => { setEditingEmployee(null); setShowForm(true); }}
          className="h-7 rounded-[3px] px-3 text-[12.5px] font-semibold"
          style={{ background: 'var(--accent)', color: 'var(--on-accent)' }}
        >
          New
        </button>
      )}
    </div>
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      let clickedInsideDropdown = false;

      Object.values(dropdownRefs.current).forEach(ref => {
        if (ref && ref.contains(event.target)) {
          clickedInsideDropdown = true;
        }
      });

      if (!clickedInsideDropdown) {
        setActionDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const activeCount = employees.filter(e => e.isActive).length;

  return (
    <div className="min-h-full" style={{ background: 'var(--bg)' }}>
      {/* ══ CONTROL PANEL ══ */}
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-[14px] pb-2 pt-[9px]">
          <h1
            className="m-0 truncate text-[18px] font-semibold tracking-[-.2px]"
            style={{ color: 'var(--ink)' }}
          >
            Employees
          </h1>

          <div className="flex items-center gap-[6px]">
            <button
              onClick={() => { setEditingEmployee(null); setShowForm(true); }}
              className="h-7 rounded-[3px] px-[13px] text-[12.5px] font-semibold"
              style={{ background: 'var(--accent)', color: 'var(--on-accent)', border: 0, cursor: 'pointer' }}
            >
              New
            </button>
            <button onClick={handleDownloadTemplate} style={ghostBtn}>
              <DocumentArrowDownIcon className="h-[13px] w-[13px]" />
              Template
            </button>
            <button onClick={() => setUploadModal(true)} style={ghostBtn}>
              <DocumentArrowUpIcon className="h-[13px] w-[13px]" />
              Import
            </button>
          </div>

          <div className="min-w-[100px] flex-1" />

          {/* Search — Odoo control-panel style: one hairline box, facets inside */}
          <div
            className="flex min-w-[200px] max-w-[420px] flex-1 items-center gap-[6px] rounded-[3px] py-[2px] pl-2 pr-1"
            style={{ background: 'var(--surface)', border: '1px solid var(--line-3)' }}
          >
            <svg
              className="h-[13px] w-[13px] flex-shrink-0"
              style={{ color: 'var(--faint)' }}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              placeholder="Search name, email or code…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="h-6 min-w-[70px] flex-1 border-0 bg-transparent text-[12.5px] outline-none"
              style={{ color: 'var(--body)' }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="px-1 text-[14px] leading-none"
                style={{ color: 'var(--faint)', background: 'transparent', border: 0, cursor: 'pointer' }}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>

          {/* Record counter, in place of the old pink stat pills */}
          <div className="flex items-center gap-[10px] text-[12px]" style={{ color: 'var(--muted)' }}>
            <span>
              {filteredEmployees.length}{search ? ` / ${employees.length}` : ''}
              {' '}record{filteredEmployees.length === 1 ? '' : 's'}
            </span>
            <span style={{ width: 1, height: 15, background: 'var(--line-3)' }} />
            <span>{activeCount} active</span>
          </div>
        </div>
      </div>

      <div className="p-[14px]">
        {/* Form */}
        {showForm && (
          <div className="mb-[14px]">
            <EmployeeForm
              initialData={editingEmployee}
              isEditing={!!editingEmployee}
              onSubmit={async (data) => {
                if (editingEmployee) await employeeService.updateEmployee(editingEmployee.id, data);
                else await employeeService.createEmployee(data);
                setShowForm(false);
                await loadEmployees();
              }}
              onCancel={() => setShowForm(false)}
            />
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div
            className="mb-[14px] flex items-center gap-2 rounded-[4px] px-3 py-2.5 text-[12.5px]"
            style={{ background: 'var(--b-red-bg)', border: '1px solid var(--line)', color: 'var(--b-red-fg)' }}
          >
            <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {/* Table */}
        <div
          className="overflow-hidden rounded-[4px]"
          style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow-sm)' }}
        >
          <DataTable
            columns={columns}
            data={filteredEmployees}
            progressPending={loading}
            progressComponent={<LoadingComponent />}
            noDataComponent={<NoDataComponent />}
            customStyles={customStyles}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            highlightOnHover
            pointerOnHover
            responsive
            persistTableHead
          />
        </div>
      </div>

      {/* Upload Modal */}
      {uploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            className="w-full max-w-md rounded-[5px] p-5"
            style={{ background: 'var(--surface)', border: '1px solid var(--line)', boxShadow: 'var(--shadow)' }}
          >
            <h3 className="mb-4 text-[15px] font-semibold" style={{ color: 'var(--ink)' }}>
              Import employees
            </h3>

            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-[12.5px] font-semibold" style={{ color: 'var(--body-2)' }}>
                  CSV file
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="w-full rounded-[3px] px-2.5 py-2 text-[12.5px] outline-none"
                  style={{ background: 'var(--surface)', border: '1px solid var(--line-3)', color: 'var(--body)' }}
                />
                <p className="mt-1.5 text-[11.5px]" style={{ color: 'var(--faint)' }}>
                  Download the template first, fill it with employee data, then upload here.
                </p>
              </div>

              {uploadResults && (
                <div
                  className="rounded-[4px] px-3 py-2.5 text-[12.5px] font-medium"
                  style={
                    uploadResults.errorCount > 0
                      ? { background: 'var(--b-amber-bg)', color: 'var(--b-amber-fg)', border: '1px solid var(--line)' }
                      : { background: 'var(--b-green-bg)', color: 'var(--b-green-fg)', border: '1px solid var(--line)' }
                  }
                >
                  {uploadResults.successCount} successful, {uploadResults.errorCount} errors
                </div>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={() => {
                    setUploadModal(false);
                    setUploadFile(null);
                    setUploadResults(null);
                  }}
                  style={ghostBtn}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadSubmit}
                  disabled={!uploadFile || loading}
                  className="h-7 rounded-[3px] px-[13px] text-[12.5px] font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ background: 'var(--accent)', color: 'var(--on-accent)', border: 0, cursor: 'pointer' }}
                >
                  {loading ? 'Uploading…' : 'Import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeList;
